/**
 * Main App component.
 * Handles authentication and routing to LoginPage or MainDashboard.
 * Includes theme management.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, IconButton, Box } from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LoginPage from './components/LoginPage';
import MainDashboard from './components/MainDashboard';
import { getToken, getPreferences, savePreferences } from './services/storage';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    // Check if user has a valid token
    const token = getToken();
    setIsAuthenticated(!!token);
    
    // Load theme preference
    const prefs = getPreferences();
    setThemeMode(prefs.theme);
    
    setIsLoading(false);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    
    // Save preference
    const prefs = getPreferences();
    prefs.theme = newMode;
    savePreferences(prefs);
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
        },
        typography: {
          fontSize: 13,
          h1: {
            fontSize: '2rem',
          },
          h2: {
            fontSize: '1.75rem',
          },
          h3: {
            fontSize: '1.5rem',
          },
          h4: {
            fontSize: '1.25rem',
          },
          h5: {
            fontSize: '1.1rem',
          },
          h6: {
            fontSize: '1rem',
          },
          body1: {
            fontSize: '0.875rem',
          },
          body2: {
            fontSize: '0.8125rem',
          },
          caption: {
            fontSize: '0.75rem',
          },
          button: {
            fontSize: '0.8125rem',
          },
        },
      }),
    [themeMode]
  );

  if (isLoading) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {isAuthenticated && (
        <Box
          sx={{
            position: 'fixed',
            top: 8,
            right: 60,
            zIndex: 1300,
          }}
        >
          <IconButton onClick={toggleTheme} color="inherit">
            {themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      )}
      {isAuthenticated ? (
        <MainDashboard />
      ) : (
        <LoginPage onLoginSuccess={handleLoginSuccess} />
      )}
    </ThemeProvider>
  );
}

export default App;

